import { SEASON } from "../siteConfig";
import "./halloween.css";
import "../christmas/christmas.css";

/**
 * Seasonal dress-up for the public site and the realtor panel.
 *
 * SEASON in siteConfig.js picks it: "auto" follows the calendar (see
 * seasonFor), "halloween" or "christmas" pins one, and "" switches all of it
 * off — no sprite rendered, no font downloaded, the site exactly its everyday
 * self. `?season=off|halloween|christmas` in the address overrides it for that
 * visit, to preview any look without a deploy.
 */

/**
 * The calendar behind "auto", in the visitor's own time zone: Halloween for all
 * of October; Christmas from 1 December through 14 January, which takes in
 * Christmas on the 25th, New Year and the Old New Year.
 */
export function seasonFor(date) {
  var month = date.getMonth(); // 0 = January
  if (month === 9) return "halloween";
  if (month === 11 || (month === 0 && date.getDate() <= 14)) return "christmas";
  return "";
}

function resolveSeason() {
  var season = SEASON;
  try {
    var override = new URLSearchParams(window.location.search).get("season");
    if (override === "off") season = "";
    else if (override) season = override;
  } catch (err) {
    // No window (tests) or a browser without URLSearchParams: keep the setting.
  }
  return season === "auto" ? seasonFor(new Date()) : season;
}

var ACTIVE = resolveSeason();

export var HALLOWEEN = ACTIVE === "halloween";
export var CHRISTMAS = ACTIVE === "christmas";

/** The night palette every Halloween piece draws from. */
export var NIGHT = {
  deep: "#07061A",
  sky: "#120D2A",
  dusk: "#2A1650",
  haze: "#3B1D52",
  plum: "#4A2F7A",
  pumpkin: "#FF7A1A",
  pumpkinDeep: "#E4570B",
  ember: "#FFB347",
  candle: "#FFD46B",
  moon: "#FFF4D6",
  bone: "#F7F3EA",
};

/** The winter palette the Christmas pieces draw from. */
export var FROST = {
  // The top row of the parallax background art: anything that has to read as
  // the same sky (the hero, the header, the maintenance page) uses this.
  sky: "#02275E",
  deep: "#011A42",
  navy: "#0A2C66",
  gold: "#FFCF5A",
  goldDeep: "#E0A526",
  red: "#E63946",
  redDeep: "#A3182C",
  pine: "#0E4A35",
};

/**
 * Display face for the odd spooky word. It has Cyrillic, so Ukrainian headings
 * keep it — most "horror" fonts are Latin-only and would fall back silently.
 * One weight only: set fontWeight 400 wherever it is used, or the browser
 * fakes a bold and smears the drips.
 */
export var SPOOKY_FONT = '"Rubik Wet Paint", "Manrope", system-ui, sans-serif';

/**
 * Christmas's pixel display face, to match the art. Press Start 2P because its
 * Cyrillic actually reads: Pixelify Sans, the obvious pick, draws з like э and
 * д like а, so "здали" came out as nonsense. One weight and very wide: set
 * fontWeight 400 and size it down (about 0.7em next to normal text).
 */
export var PIXEL_FONT = '"Press Start 2P", "Manrope", system-ui, sans-serif';

/**
 * Header and app-bar colours while a season is on, or null: both seasons put
 * the site's chrome into a night sky, each with its own accent.
 */
export var CHROME = HALLOWEEN
  ? {
      bar: NIGHT.deep,
      barScrolled: "rgba(18,13,42,0.9)",
      panelBar: "rgba(10,8,28,0.94)",
      border: "rgba(255,122,26,0.28)",
      accent: NIGHT.pumpkin,
      accentText: NIGHT.ember,
      accentBg: "rgba(255,122,26,0.14)",
      accentHover: "rgba(255,122,26,0.1)",
      chipBg: "rgba(255,122,26,0.16)",
    }
  : CHRISTMAS
  ? {
      bar: FROST.sky,
      barScrolled: "rgba(2,39,94,0.92)",
      panelBar: "rgba(2,33,82,0.95)",
      border: "rgba(255,207,90,0.3)",
      accent: FROST.gold,
      accentText: "#FFD978",
      accentBg: "rgba(255,207,90,0.14)",
      accentHover: "rgba(255,207,90,0.1)",
      chipBg: "rgba(255,207,90,0.16)",
    }
  : null;

var DOCUMENT = {
  halloween: { font: "Rubik+Wet+Paint", rootClass: "hw-on", themeColor: NIGHT.sky },
  christmas: { font: "Press+Start+2P", rootClass: "xm-on", themeColor: FROST.sky },
};

var fontLoaded = false;

/**
 * Document-level touches: the season's display font, the class its CSS scopes
 * page-wide rules to, and night-coloured browser chrome on phones.
 *
 * Called from PublicSite and AdminApp rather than at import time; returns the
 * undo for when they unmount.
 */
export function applySeasonToDocument() {
  var look = DOCUMENT[ACTIVE];
  if (!look || typeof document === "undefined") return undefined;

  if (!fontLoaded) {
    fontLoaded = true;
    var font = document.createElement("link");
    font.rel = "stylesheet";
    font.href = "https://fonts.googleapis.com/css2?family=" + look.font + "&display=swap";
    document.head.appendChild(font);
  }

  var root = document.documentElement;
  root.classList.add(look.rootClass);

  var themeColor = document.querySelector('meta[name="theme-color"]');
  var previousColor = themeColor ? themeColor.getAttribute("content") : null;
  if (themeColor) themeColor.setAttribute("content", look.themeColor);

  return function () {
    root.classList.remove(look.rootClass);
    if (themeColor && previousColor) themeColor.setAttribute("content", previousColor);
  };
}
